import { type RefObject, useEffect, useLayoutEffect, useState } from "react";
import { beforeEachStep, carryEachStep, rememberInStep, stepKey, stepState } from "./router.ts";

// Back puts every list where it was: its scroll and what was open (DESIGN.md, Views). Each step in the
// history keeps, per list, what it looked like when it was left. Kept twice: here in memory, written as
// it changes, so Back and Forward find it at once; and in the step's own history state, so a reload
// finds it too. The history state is written sparingly (browsers limit how often a page may write it):
// a moment after scrolling stops, and always just before the next step.
//
// A view comes back where it was however I come back to it, by Back or by its tab (DESIGN.md, "An item
// opens over the view I'm on"): a view's own lists are named after it ("ideas", "ideas-suggest-waiting",
// "bookings-marked"), and where each was last left in a trip is kept apart from the steps. Every new step
// in the trip starts with those (`carryEachStep`), so a view's tab opens it where it was, and so does a
// reload or the trip reopened later, from the step it opens on.

const memory = new Map<string, unknown>();
const slot = (name: string) => `${stepKey()}:${name}`;

const VIEW_LIST = /^(days|ideas|people|bookings|updates)(-|$)/;
const tripOf = (pathname: string) => pathname.match(/^\/t\/([^/]+)\/?$/)?.[1] ?? null;
// Each trip's views' lists as last left, while the page is open, by trip; started from the step the page
// opened on.
const latest = new Map<string, Map<string, unknown>>();
function latestHere(): Map<string, unknown> | null {
  const trip = tripOf(location.pathname);
  if (!trip) return null;
  let lists = latest.get(trip);
  if (!lists) {
    lists = new Map(Object.entries(stepState().lists ?? {}).filter(([name]) => VIEW_LIST.test(name)));
    latest.set(trip, lists);
  }
  return lists;
}
carryEachStep((to) => {
  const here = tripOf(location.pathname);
  if (!here || tripOf(new URL(to, location.origin).pathname) !== here) return {};
  return Object.fromEntries(latestHere() ?? []);
});

// The last thing opened from a view, marked in it until another is opened there: kept as the view's
// scroll is, while the trip is open and when it reopens on this device.
export function markOpened(view: string, id: string) {
  write(`${view}-marked`, id);
}
export function markedIn(view: string): string | null {
  const id = latestHere()?.get(`${view}-marked`);
  return typeof id === "string" ? id : null;
}

function read<T>(name: string): T | undefined {
  const key = slot(name);
  if (memory.has(key)) return memory.get(key) as T;
  return stepState().lists?.[name] as T | undefined;
}

// Pending writes to the history state, by step and list.
const pending = new Map<string, { step: string; name: string; value: unknown }>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (timer) clearTimeout(timer);
  timer = null;
  const step = stepKey();
  const lists: Record<string, unknown> = {};
  for (const p of pending.values()) if (p.step === step) lists[p.name] = p.value;
  pending.clear();
  if (Object.keys(lists).length > 0) rememberInStep({ lists: { ...stepState().lists, ...lists } });
}
beforeEachStep(flush);
// And when the page goes away or into the background (the app closed, or switched from), so the trip
// reopens where it was (reopen.ts).
addEventListener("pagehide", flush);
// And before a reload: Chrome keeps no history state written as the page hides for a reload, so a list
// that moved in the last moment before one (a view laid out narrower as a card opened beside it, a step
// just gone back and forward to) came back where it had been before (found 2026-10-22 by back.spec.ts:
// reloaded with a hotel's card over Ideas, Ideas came back 368 px off once the card closed).
addEventListener("beforeunload", flush);
document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());

function write(name: string, value: unknown, soon = false) {
  const step = stepKey();
  memory.set(`${step}:${name}`, value);
  if (VIEW_LIST.test(name)) latestHere()?.set(name, value);
  pending.set(`${step}:${name}`, { step, name, value });
  if (!soon) return flush();
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, 250);
}

// Something a list has open or closed (a section expanded, a tab picked), kept with the step.
export function useKept<T>(name: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => read<T>(name) ?? initial);
  return [
    value,
    (next: T) => {
      setValue(next);
      write(name, next);
    },
  ];
}

// What a list shows something selected by (the map's list: its lit row), what's there while something is
// selected, so its row is waited for (a group opening to show it), and what sticks at the list's top over
// what scrolls under it (its search).
export interface Focus {
  selector: string;
  wanted: string;
  under?: string;
}

// Whether `el` shows whole in the scrolling list, below what sticks at its top.
export function wholeIn(list: HTMLElement, el: Element, under?: string): boolean {
  const r = el.getBoundingClientRect();
  const l = list.getBoundingClientRect();
  const top = Math.max(l.top, (under && list.querySelector(under)?.getBoundingClientRect().bottom) || l.top);
  return r.top >= top - 1 && r.bottom <= l.bottom + 1;
}

// `el` in the middle of the list (what of it shows below what sticks at its top), at once.
function toMiddle(list: HTMLElement, el: Element, under?: string) {
  const r = el.getBoundingClientRect();
  const l = list.getBoundingClientRect();
  const top = Math.max(l.top, (under && list.querySelector(under)?.getBoundingClientRect().bottom) || l.top);
  list.scrollTop += r.top + r.height / 2 - (top + l.bottom) / 2;
}

// A scrolling list's position, kept with the step and put back when the step comes back. `name` says
// which list (and, where one panel shows different things, which of them). What loads later (a feed)
// is waited for, until the list is as tall as it was or the person scrolls it themselves (or something in it takes the
// focus). With `focus`,
// what's selected in it is in view as it shows, in its middle when it isn't already whole in view, there
// at once and never scrolled to in front of me (features/map.md, asked 2026-10-19), also when it shows
// a moment later (its group opening): a list already showing scrolls to a new selection itself. With
// `startAt`, a list with nothing kept (first shown in the trip) starts with what it matches at its top (Days,
// during the trip: today), rather than at its own top.
export function useKeptScroll(ref: RefObject<HTMLElement | null>, name: string, focus?: Focus, startAt?: string) {
  const selector = focus?.selector;
  const wanted = focus?.wanted;
  const under = focus?.under;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const kept = read<number>(name);
    // Where it starts: as it was left, else what `startAt` matches at its top (once it's there), else its top.
    const start = () => {
      const at = kept === undefined && startAt ? el.querySelector(startAt) : null;
      if (!at) return kept ?? 0;
      const top = at.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
      return Math.min(Math.max(0, Math.round(top)), el.scrollHeight - el.clientHeight);
    };
    let target = start();
    el.scrollTop = target;
    // What's selected, in view (moved there if need be); false while it isn't there yet.
    const place = () => {
      const selected = selector ? el.querySelector(selector) : null;
      if (!selected) return false;
      if (!wholeIn(el, selected, under)) toMiddle(el, selected, under);
      return true;
    };
    // Done once what's selected is in view; with nothing selected, once the list is where it was and its
    // fonts have loaded (a page just opened draws in the fallback font first: the text taking its own font
    // a moment later would leave the list a pixel or two from where it was).
    const fontsLoading = () => document.fonts?.status === "loading";
    const done = () =>
      selector && wanted && el.querySelector(wanted)
        ? place()
        : Math.abs(el.scrollTop - target) <= 1 && !fontsLoading();
    if (done()) return;
    let settling = true;
    const settle = () => {
      if (!settling) return;
      target = start();
      el.scrollTop = target;
      if (done()) stop();
    };
    document.fonts?.ready.then(settle);
    const grown = new MutationObserver(settle);
    grown.observe(el, { childList: true, subtree: true });
    const resized = new ResizeObserver(settle);
    for (const child of el.children) resized.observe(child);
    const giveUp = setTimeout(() => stop(), 3000);
    // The person acting in it (or a change they chose elsewhere taking the focus into it, as Add's "Add a to-do"
    // opening its form at Bookings' foot does) ends the wait: what they're at stays where it is.
    const events = ["wheel", "touchstart", "keydown", "pointerdown", "focusin"] as const;
    function stop() {
      settling = false;
      grown.disconnect();
      resized.disconnect();
      clearTimeout(giveUp);
      for (const e of events) el?.removeEventListener(e, stop);
    }
    for (const e of events) el.addEventListener(e, stop, { passive: true });
    return stop;
  }, [ref, name, selector, wanted, under, startAt]);

  // Written as it scrolls, and read again as the step is left: a list left the moment it moved (a tap
  // right after a fling, or a background tab, which draws no frames) hasn't reported its scroll yet.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => write(name, el.scrollTop, true);
    el.addEventListener("scroll", onScroll, { passive: true });
    const leaving = beforeEachStep(() => write(name, el.scrollTop));
    return () => {
      el.removeEventListener("scroll", onScroll);
      leaving();
    };
  }, [ref, name]);
}

// What a list looked like when its step was first shown (which sections had something waiting), kept
// with the step while the page stays open: Back and Forward find the same, and so does coming back to its
// view by the view's tab, as the view's other lists do; opening the page again, or reloading it, looks
// afresh.
const firsts = new Map<string, unknown>();
export function useKeptFirst<T>(name: string, first: () => T): T {
  const trip = tripOf(location.pathname);
  const asLeft = trip && VIEW_LIST.test(name) ? `${trip}:${name}` : null;
  const [value] = useState<T>(
    () =>
      (memory.get(slot(name)) as T | undefined) ??
      (asLeft ? (firsts.get(asLeft) as T | undefined) : undefined) ??
      first(),
  );
  useEffect(() => {
    const key = slot(name);
    if (!memory.has(key)) memory.set(key, value);
    if (asLeft) firsts.set(asLeft, value);
  }, [name, value, asLeft]);
  return value;
}

// What I've folded or unfolded on this device (Ideas' and Bookings' sections, Bookings' kinds, a choice's
// options and a decided one's other options, Suggestions' kinds, Your trips' Recently deleted), under `key` (reopen.ts's keptKey: per account, and per trip where it's a trip's).
// Kept in this browser only, so it's the same through Back and after a reload: a convenience; it may be
// cleared, or blocked in a private window, and signing out forgets it.
// Ideas and Bookings keep theirs under the same key (each section and group by its own name), so a fold is
// written over what's stored now, never over what this view read when it opened.
export function useFolds(key: string) {
  const stored = (): Record<string, boolean> => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "{}") as Record<string, boolean>;
    } catch {
      return {};
    }
  };
  const [chosen, setChosen] = useState<Record<string, boolean>>(stored);
  return {
    folded: (id: string, otherwise: boolean) => chosen[id] ?? otherwise,
    set: (id: string, folded: boolean) => {
      const next = { ...chosen, ...stored(), [id]: folded };
      setChosen(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Not kept: it still folds for now.
      }
    },
  };
}
